import { usePostHog } from '@posthog/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import DynamicZone from '../components/DynamicZone';
import ComponentDebugOverlay from '../components/debug/ComponentDebugOverlay';
import { getPage, getDomain } from '../data/data-layer';
import HeadManager from '../services/HeadManager';
import { useDomain } from '../contexts/domain-provider';
import PageContextProvider from '../contexts/page-provider';

const isStaticFileOrMalicious = (path: string) => {
    const extensionMatch = /\.(sql|conf|json|php|zip|xml|txt|env|yml|yaml|ini|bak|git|js|css|png|jpg|jpeg|gif|ico|svg|woff|woff2|ttf|otf|map)$/i.test(path);
    const scannerMatch = /(wp-admin|wp-content|well-known|\.git|cgi-bin|actuator|etc\/passwd|\.\.)/i.test(path);
    return extensionMatch || scannerMatch;
};

export const Route = createFileRoute('/$')({
    beforeLoad: async ({ params, context }) => {
        const slug = "/" + params._splat;

        if (isStaticFileOrMalicious(slug)) {
            throw redirect({
                to: "/",
                replace: true,
            });
        }

        const queryClient = context.queryClient;
        const domainName = context.domainName;

        // Synchronously extract the already cached domain object from root's beforeLoad
        const domainData = queryClient.getQueryData<any>(getDomain(domainName).queryKey);
        const domain = domainData?.data?.[0];

        // Prefetch the dynamic page using prefetchQuery to prime the cache
        await queryClient.prefetchQuery(getPage(slug, domain));

        // Synchronously extract the prefetched page data from the cache for metadata/title
        const pageData = queryClient.getQueryData<any>(getPage(slug, domain).queryKey);
        const entity = pageData?.data?.[0];

        return {
            title: entity?.title,
            metadata: entity?.metadata,
        };
    },
    head: (ctx) => {
        return HeadManager.routerHead(ctx);
    },
    component: RouteComponent,
    ssr: true
})

function RouteComponent() {
    const { _splat } = Route.useParams();
    const slug = "/" + _splat;
    const { domain } = useDomain();
    const { data } = useSuspenseQuery(getPage(slug, domain));
    const router = useRouterState();
    const posthog = usePostHog();
    useEffect(() => {
        if (!data?.data || data.data.length === 0) {
            posthog.capture("Not found", { location: router.location });
            const timeout = setTimeout(() => (window.location.href = "/"), 2000);
            return () => clearTimeout(timeout);
        }
    }, [data, posthog, router.location]);

    const entity = data?.data?.[0];

    interface ConfiguredContentBlock {
        id: number;
        __component: string;
        editor_label?: string;
        html_id?: string;
        configuration?: any;
        content_block?: {
            id: number;
            documentId: string;
            name?: string;
            title?: string;
            elements?: any[];
        };
    }

    if (entity) {
        return (
            <PageContextProvider page={entity}>
                {(entity?.configured_content_blocks as ConfiguredContentBlock[] | undefined)?.map((e, i) => {
                    const blockTitle = e.content_block?.name || e.content_block?.title;
                    const pageAdminUrl = entity.documentId
                        ? `https://cms.purebotswana.com/admin/content-manager/collection-types/api::page.page/${entity.documentId}`
                        : undefined;
                    const content = (
                        <ComponentDebugOverlay
                            key={i}
                            label="ConfiguredContentBlock"
                            title={e.editor_label}
                            adminUrl={pageAdminUrl}
                        >
                            <DynamicZone
                                title={blockTitle}
                                elements={e.content_block?.elements}
                                configuration={e.configuration}
                                contentBlockDocumentId={e.content_block?.documentId}
                                pageDocumentId={entity.documentId}
                            />
                        </ComponentDebugOverlay>
                    );
                    return e.html_id ? (
                        <div id={e.html_id} key={i}>
                            {content}
                        </div>
                    ) : (
                        content
                    );
                })}
            </PageContextProvider>
        );
    }

    return (
        <div style={{ padding: "1em" }}>
            <p>
                Requested resource `{router.location.pathname}` not found. Redirecting
                to the <a href="/">main page</a>.
            </p>
        </div>
    );
}
